import type { SluggedId, HoleBooleanMap } from "Models";
import {
  sluggedIdSchema,
  sluggedIdValidator,
  holeBooleanMapSchema,
  windowsFileNameXmlRegex,
  windowsFileNameImageRegex,
  facilityIdSchema,
  descriptionSchema,
  createdSchema,
  lastModifiedSchema,
  deletionSchema,
  nameSchema,
  errorSchema,
} from "entities/common";
import { AdKitKey } from "types/enums";
import { z } from "zod";
import { DateTime } from "luxon";

export type ClubhouseAdKitLocationInstanceInternalDto = {
  id: SluggedId;
  imageId: SluggedId;
  holeNumber: number;
  instanceId?: number;
  imageFilePath: string;
  imageUrl: string;
  widthAdjustment: number;
  heightAdjustment: number;
  created: DateTime;
  lastModified: DateTime;
  deleted: boolean;
  whenDeleted?: DateTime | null;
  orderLocation?: number;
};

export type ClubhouseAdKitLocationInternalDto = {
  id: SluggedId;
  adKitMetaDataId: AdKitKey;
  enabledHoles?: HoleBooleanMap;
  adInstances?: ClubhouseAdKitLocationInstanceInternalDto[];
  created: DateTime;
  lastModified: DateTime;
  deleted: boolean;
  whenDeleted?: DateTime | null;
  syncedSimulatorIds?: string[];
};

export type ClubhouseAdKitInternalDto = {
  id: SluggedId;
  name: string;
  description?: string | null;
  fileName: string;
  facilityId: string;
  transition: {
    display: number;
    fadeIn: number;
    fadeOut: number;
  };
  created: DateTime;
  lastModified: DateTime;
  deleted: boolean;
  whenDeleted?: DateTime | null;
  adLocations?: ClubhouseAdKitLocationInternalDto[];
  syncedSimulatorIds?: string[];
  currentlySyncedSimulatorIds?: string[];
};

const ClubhouseAdKitSyncDtoSchema = z.object({
  adKitId: z.string(),
  simulatorId: z.string(),
  success: z.boolean().optional(),
  resultMessage: z.string().optional(),
});

export const ClubhouseAdKitSyncResponseSchema = z.object({
  result: z.array(ClubhouseAdKitSyncDtoSchema),
  errors: z.array(errorSchema).optional(),
});

const ClubhouseAdKitActivationDtoSchema = z.object({
  adKitId: z.string().optional(),
  simulatorId: z.string().optional(),
});

// Errors kept loose: a 207 is a partial success, and a strict parse throwing
// on an unexpected error shape would report the sims that went live as failed.
export const ClubhouseAdKitActivationResponseSchema = z.object({
  result: z.array(ClubhouseAdKitActivationDtoSchema),
  errors: z
    .array(
      z
        .object({
          message: z.string().optional(),
          metadata: z.record(z.string(), z.unknown()).optional(),
        })
        .loose(),
    )
    .optional(),
});

export type ClubhouseAdKitSyncResponse = z.infer<
  typeof ClubhouseAdKitSyncResponseSchema
>;
export type ClubhouseAdKitActivationResponse = z.infer<
  typeof ClubhouseAdKitActivationResponseSchema
>;

const ClubhouseAdKitAdLocationInstanceDtoBaseSchema = z.object({
  ...sluggedIdSchema,
  imageId: sluggedIdValidator,
  holeNumber: z.number().int().min(0).max(18),
  instanceId: z.number().int().min(-1).optional(),
  // Show order. Was missing here, so zod stripped it and every sort on it
  // fell back to the API's list order.
  orderLocation: z.number().int().optional(),
  imageFilePath: z
    .string()
    .max(100, { error: "imageFilePath must be 100 characters or fewer" })
    .regex(windowsFileNameImageRegex, {
      error: "imageFilePath must be a valid Windows file name",
    }),
  imageUrl: z.url(),
  widthAdjustment: z.number().int().min(-1),
  heightAdjustment: z.number().int().min(-1),
  ...createdSchema,
  ...lastModifiedSchema,
});

const ClubhouseAdKitAdLocationInstanceDtoRawSchema = z.intersection(
  ClubhouseAdKitAdLocationInstanceDtoBaseSchema,
  deletionSchema,
);

const ClubhouseAdKitAdLocationDtoBaseSchema = z.object({
  ...sluggedIdSchema,
  adKitMetaDataId: z.enum(AdKitKey),
  enabledHoles: z.optional(holeBooleanMapSchema),
  adInstances: z.optional(
    z.array(ClubhouseAdKitAdLocationInstanceDtoRawSchema),
  ),
  ...createdSchema,
  ...lastModifiedSchema,
});

const ClubhouseAdKitAdLocationDtoRawSchema = z.intersection(
  ClubhouseAdKitAdLocationDtoBaseSchema,
  deletionSchema,
);

const ClubhouseAdKitAdLocationDtoListSchema = z.array(
  ClubhouseAdKitAdLocationDtoRawSchema,
);

const ClubhouseAdKitDtoBaseSchema = z.object({
  ...sluggedIdSchema,
  ...nameSchema,
  ...lastModifiedSchema,
  ...createdSchema,
  ...descriptionSchema,
  ...facilityIdSchema,
  fileName: z
    .string()
    .max(100, { error: "fileName must be 100 characters or fewer" })
    .regex(windowsFileNameXmlRegex, {
      error: "fileName must be a valid Windows file name and end in .xml",
    }),
  transition: z.object({
    display: z.number(),
    fadeIn: z.number(),
    fadeOut: z.number(),
  }),
  adLocations: z.optional(ClubhouseAdKitAdLocationDtoListSchema),
  syncedSimulatorIds: z.optional(z.array(z.string())),
  currentlySyncedSimulatorIds: z.optional(z.array(z.string())),
});

const ClubhouseAdKitDtoRawSchema = z.intersection(
  ClubhouseAdKitDtoBaseSchema,
  deletionSchema,
);

export const ClubhouseAdKitLocationInstanceDtoSchema =
  ClubhouseAdKitAdLocationInstanceDtoRawSchema.transform(
    (data): ClubhouseAdKitLocationInstanceInternalDto => ({
      ...data,
      // Brand the id, created, lastModified and whenDeleted values
      whenDeleted: data.whenDeleted as DateTime | null | undefined,
    }),
  ) satisfies z.ZodType<ClubhouseAdKitLocationInstanceInternalDto>;

export const ClubhouseAdKitLocationDtoSchema =
  ClubhouseAdKitAdLocationDtoRawSchema.transform(
    (data): ClubhouseAdKitLocationInternalDto => ({
      ...data,
      // Brand the id, created, lastModified and whenDeleted values
      whenDeleted: data.whenDeleted as DateTime | null | undefined,
    }),
  ) satisfies z.ZodType<ClubhouseAdKitLocationInternalDto>;

export const ClubhouseAdKitDtoSchema = ClubhouseAdKitDtoRawSchema.transform(
  (data): ClubhouseAdKitInternalDto => ({
    ...data,
    // Brand the id, created, lastModified and whenDeleted values
    whenDeleted: data.whenDeleted as DateTime | null | undefined,
  }),
) satisfies z.ZodType<ClubhouseAdKitInternalDto>;

export const ClubhouseAdKitResponseSchema = z.object({
  result: ClubhouseAdKitDtoSchema,
  errors: z.array(errorSchema).optional(),
});
export type ClubhouseAdKitResponse = z.infer<
  typeof ClubhouseAdKitResponseSchema
>;

export const ClubhouseAdKitLocationInstanceResponseSchema = z.object({
  result: z.boolean(),
  errors: z.array(errorSchema).optional(),
});

export type ClubhouseAdKitDto = z.infer<typeof ClubhouseAdKitDtoSchema>;
export type ClubhouseAdKitLocationDto = z.infer<
  typeof ClubhouseAdKitLocationDtoSchema
>;
export type ClubhouseAdKitLocationInstanceDto = z.infer<
  typeof ClubhouseAdKitLocationInstanceDtoSchema
>;

export const ClubhouseAdKitDtoArraySchema = z.array(
  ClubhouseAdKitDtoSchema,
) satisfies z.ZodType<ClubhouseAdKitDto[]>;

export const ClubhouseAdKitLocationDtoArraySchema = z.array(
  ClubhouseAdKitLocationDtoSchema,
) satisfies z.ZodType<ClubhouseAdKitLocationDto[]>;

export const ClubhouseAdKitLocationInstanceDtoArraySchema = z.array(
  ClubhouseAdKitLocationInstanceDtoSchema,
) satisfies z.ZodType<ClubhouseAdKitLocationInstanceDto[]>;

// GET /api/ad-kit/metadata — the placement catalog. Read-only reference data,
// so no id/created/deleted fields and nothing to brand.
export const ClubhouseAdKitMetaDataDtoSchema = z.object({
  id: z.enum(AdKitKey),
  key: z.string(),
  width: z.number().int().optional(),
  height: z.number().int().optional(),
  aspectRatio: z.number(),
  isHoleEnabled: z.boolean(),
  hasDynamicRotation: z.boolean(),
  hasTransitions: z.boolean(),
  canAssignToSpecificHoles: z.boolean().optional(),
  defaultFadeIn: z.number().nullish(),
  defaultDisplay: z.number().nullish(),
  defaultFadeOut: z.number().nullish(),
  description: z.string().optional(),
  name: z.string(),
  sampleUrls: z.record(z.string(), z.string()).optional(),
});

export type ClubhouseAdKitMetaDataDto = z.infer<
  typeof ClubhouseAdKitMetaDataDtoSchema
>;

export const ClubhouseAdKitSamplesDtoSchema = z.object({
  adKitMetaDatas: z.array(ClubhouseAdKitMetaDataDtoSchema).optional(),
  exampleImageUrls: z.record(z.string(), z.string()).optional(),
});

export type ClubhouseAdKitSamplesDto = z.infer<
  typeof ClubhouseAdKitSamplesDtoSchema
>;
